import type { AudioAnnotationField } from '@/features/nectar/api/models'

type FieldValidator = (field: AudioAnnotationField, value: unknown) => boolean

const isValidSingleSelect: FieldValidator = (field, value) => {
  if (field.type !== 'single-select') return true
  if (value === undefined || value === null || value === '') return !field.required
  return typeof value === 'string' && field.options.some((o) => o.value === value)
}

const isValidMultiSelect: FieldValidator = (field, value) => {
  if (field.type !== 'multi-select') return true
  if (!Array.isArray(value)) return !field.required
  const allValid = value.every((v) => typeof v === 'string' && field.options.some((o) => o.value === v))
  if (!allValid) return false
  if (field.required && value.length === 0) return false
  if (field.minSelected !== undefined && value.length < field.minSelected) return false
  if (field.maxSelected !== undefined && value.length > field.maxSelected) return false
  return true
}

const isValidToggle: FieldValidator = (field, value) => {
  if (field.type !== 'toggle') return true
  if (value === undefined || value === null) return !field.required
  return typeof value === 'boolean'
}

const isValidRating: FieldValidator = (field, value) => {
  if (field.type !== 'rating') return true
  if (value === undefined || value === null) return !field.required
  if (typeof value !== 'number' || !Number.isInteger(value)) return false
  return value >= field.min && value <= field.max
}

const VALIDATORS = new Map<AudioAnnotationField['type'], FieldValidator>([
  ['single-select', isValidSingleSelect],
  ['multi-select', isValidMultiSelect],
  ['toggle', isValidToggle],
  ['rating', isValidRating]
])

export const getDefaultAnswer = (field: AudioAnnotationField): unknown => {
  if (field.type === 'multi-select') return []
  return undefined
}

export const validateAnswers = (fields: AudioAnnotationField[], answers: Record<string, unknown>): boolean =>
  fields.every((field) => {
    const validator = VALIDATORS.get(field.type)
    return validator ? validator(field, answers[field.key]) : true
  })
